import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Paper,
  Avatar,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import {
  ShoppingCart as ShopIcon,
  History as HistoryIcon,
  LocalShipping as ShippingIcon,
  Assessment as AssessmentIcon,
  Storefront as StoreIcon,
  Launch as LaunchIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingIcon,
  Star as StarIcon,
  LocalOffer as OfferIcon
} from '@mui/icons-material';
import { authAPI } from '../../services/api';

const ClientDashboard = ({ currentUser }) => {
  const navigate = useNavigate();
  const [orders] = useState([]); // Will be fetched from API
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch user profile data on mount
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        const response = await authAPI.getCurrentUser();
        
        if (response.success && response.data) {
          setUserProfile(response.data);
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, []);

  // Current provider info - use actual user profile data
  const profile = userProfile?.profile || {};
  const currentProvider = {
    name: currentUser?.name || (userProfile?.firstName && userProfile?.lastName 
      ? `${userProfile.firstName} ${userProfile.lastName}` 
      : 'Healthcare Provider'),
    clinicName: profile.clinicName || 'Clinic',
    status: userProfile?.status || 'Active',
    id: currentUser?.id || userProfile?.id || 'client-id',
    address: {
      city: profile.city || '',
      state: profile.state || ''
    }
  };

  // Filter orders for current provider
  const providerOrders = orders.filter(order => order.clientId === currentProvider.id);

  // Calculate stats
  const totalOrders = providerOrders.length;
  const pendingOrders = providerOrders.filter(order => order.status === 'Pending').length;
  const fulfilledOrders = providerOrders.filter(order => order.status === 'Fulfilled').length;
  const processingOrders = providerOrders.filter(order => order.status === 'Processing').length;

  // Recent orders (last 5)
  const recentOrders = providerOrders
    .sort((a, b) => new Date(b.submissionDate) - new Date(a.submissionDate))
    .slice(0, 5);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending':
        return 'warning';
      case 'Processing':
        return 'info';
      case 'Fulfilled':
        return 'success';
      default:
        return 'default';
    }
  };

  const StatCard = ({ title, value, icon, color, description, action }) => (
    <Card sx={{ height: '100%', position: 'relative' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, }}>
          <Box>
            <Typography color="text.secondary" variant="h6" component="div">
              {title}
            </Typography>
            <Typography variant="h4" component="div" sx={{ color, fontWeight: 700 }}>
              {value}
            </Typography>
            {description && (
              <Typography variant="body2" color="text.secondary">
                {description}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              backgroundColor: `${color}15`,
              borderRadius: 2,
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {React.cloneElement(icon, { sx: { color, fontSize: 28 } })}
          </Box>
        </Box>
        {action && (
          <Box sx={{ mt: 2 }}>
            {action}
          </Box>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box
      sx={{
        pt: { xs: 7, md: 3 }, // this pushes *everything* down on mobile
        minHeight: '100vh',
      }}
    >
      <Box>
        {/* Welcome Header */}
        <Card sx={{ mb: 4, background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)', overflow: 'hidden', position: 'relative' }}>
          <CardContent sx={{ p: 4, position: 'relative' }}>
            {/* Background decoration */}
            <Box
              sx={{
                position: 'absolute',
                top: -20,
                right: -20,
                width: 120,
                height: 120,
                borderRadius: '50%',
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                zIndex: 0
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: -30,
                right: 50,
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: 'rgba(255, 255, 255, 0.05)',
                zIndex: 0
              }}
            />

            <Grid container spacing={3} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
              <Grid item xs={12} md={8}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ mr: 3, width: 64, height: 64, bgcolor: 'rgba(255, 255, 255, 0.2)', border: '2px solid rgba(255, 255, 255, 0.3)' }}>
                    <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
                      {currentProvider.name.split(' ').map(n => n[0]).join('')}
                    </Typography>
                  </Avatar>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: 'white' }}>
                      Welcome to UnisynHub, {currentProvider.name.split(' ')[0]}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 400, color: 'rgba(255, 255, 255, 0.9)' }}>
                      {currentProvider.clinicName}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 2 }}>
                      {(currentProvider.address.city || currentProvider.address.state) && (
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                          {[currentProvider.address.city, currentProvider.address.state].filter(Boolean).join(', ')}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <StarIcon sx={{ fontSize: 16, color: '#fbbf24', mr: 0.5 }} />
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600 }}>
                          VIP Member
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 2, fontSize: '1.1rem' }}>
                  Your trusted source for premium regenerative medicine products. Shop, track, and manage everything in one place.
                </Typography>
              </Grid>
              <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<ShopIcon />}
                  onClick={() => navigate('/client/place-order')}
                  sx={{
                    py: 2,
                    px: 4,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.3)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)'
                    },
                    transition: 'all 0.3s ease',
                    mb: 2
                  }}
                >
                  Shop Products
                </Button>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.9rem' }}>
                  Free shipping on orders over $500
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Shopping Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Orders"
              value={totalOrders}
              icon={<HistoryIcon />}
              color="#0d9488"
              description="lifetime purchases"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Pending Orders"
              value={pendingOrders}
              icon={<TrendingIcon />}
              color="#f59e0b"
              description="being processed"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="In Transit"
              value={processingOrders}
              icon={<ShippingIcon />}
              color="#3b82f6"
              description="on the way"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Delivered"
              value={fulfilledOrders}
              icon={<StarIcon />}
              color="#10b981"
              description="successfully delivered"
              action={
                fulfilledOrders > 0 && (
                  <Button size="small" variant="outlined" fullWidth sx={{ borderColor: '#10b981', color: '#10b981' }}>
                    View Deliveries
                  </Button>
                )
              }
            />
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          {/* Recent Orders */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                  <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                    Recent Orders
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Refresh">
                      <IconButton size="small">
                        <RefreshIcon />
                      </IconButton>
                    </Tooltip>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => navigate('/client/orders')}
                    >
                      View All Orders
                    </Button>
                  </Box>
                </Box>

                {recentOrders.length > 0 ? (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                          <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Order ID</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Patient</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Product</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600, color: '#374151' }}>Qty</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600, color: '#374151' }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Date</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600, color: '#374151' }}>Track</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {recentOrders.map((order) => (
                          <TableRow key={order.id} hover sx={{ '&:hover': { bgcolor: '#f0fdfa' } }}>
                            <TableCell sx={{ fontWeight: 600, fontFamily: 'monospace', color: '#0d9488' }}>
                              #{order.orderId}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 500 }}>
                              {order.patientInfo.firstName} {order.patientInfo.lastName}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <StoreIcon sx={{ mr: 1, fontSize: 16, color: '#6b7280' }} />
                                {order.orderInfo.product}
                              </Box>
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 600 }}>
                              {order.orderInfo.quantity}
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={order.status}
                                color={getStatusColor(order.status)}
                                size="small"
                                sx={{ fontWeight: 600 }}
                              />
                            </TableCell>
                            <TableCell>
                              {new Date(order.submissionDate).toLocaleDateString()}
                            </TableCell>
                            <TableCell align="center">
                              {order.trackingNumber ? (
                                <Tooltip title="Track Package">
                                  <IconButton
                                    size="small"
                                    onClick={() => window.open(`https://www.fedex.com/fedextrack/?trknbr=${order.trackingNumber}`, '_blank')}
                                    sx={{
                                      color: '#0d9488',
                                      '&:hover': {
                                        bgcolor: '#f0fdfa'
                                      }
                                    }}
                                  >
                                    <LaunchIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                <Chip label="Preparing" size="small" sx={{ fontSize: '0.7rem', bgcolor: '#fef3c7', color: '#d97706' }} />
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <StoreIcon sx={{ fontSize: 80, color: '#cbd5e1', mb: 2 }} />
                    <Typography variant="h5" sx={{ color: '#64748b', fontWeight: 600, mb: 1 }}>
                      Start Shopping!
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#94a3b8', mb: 4, maxWidth: 400, mx: 'auto' }}>
                      Discover our premium regenerative medicine products and place your first order today.
                    </Typography>
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<ShopIcon />}
                      onClick={() => navigate('/client/place-order')}
                      sx={{
                        bgcolor: '#0d9488',
                        '&:hover': { bgcolor: '#0f766e' },
                        px: 4,
                        py: 1.5,
                        fontWeight: 600
                      }}
                    >
                      Browse Products
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Quick Actions & Info */}
          <Grid item xs={12}>
            <Grid container spacing={3}>
              {/* Quick Actions */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#0f172a' }}>
                      Most Recent Order
                    </Typography>
                    {recentOrders.length > 0 ? (
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#0369a1', mb: 1 }}>
                          Order #{recentOrders[0].orderNumber || recentOrders[0].id?.slice(-6)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Placed on {new Date(recentOrders[0].submissionDate || recentOrders[0].createdAt).toLocaleDateString()}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="body2">
                            Items: {recentOrders[0].items?.length || 1}
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#0c4a6e' }}>
                            ${recentOrders[0].totalAmount || recentOrders[0].amount || 'N/A'}
                          </Typography>
                        </Box>
                        <Button
                          variant="outlined"
                          size="small"
                          fullWidth
                          startIcon={<LaunchIcon />}
                          onClick={() => navigate('/client/orders')}
                          sx={{
                            borderColor: '#0284c7',
                            color: '#0284c7',
                            '&:hover': { borderColor: '#0369a1', bgcolor: '#f0f9ff' }
                          }}
                        >
                          View Order Details
                        </Button>
                      </Box>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          No recent orders found
                        </Typography>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<ShopIcon />}
                          onClick={() => navigate('/client/place-order')}
                          sx={{ mt: 2, bgcolor: '#0284c7', '&:hover': { bgcolor: '#0369a1' } }}
                        >
                          Place Your First Order
                        </Button>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>


            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default ClientDashboard; 